import { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
    motion,
    useScroll,
    useTransform,
    useReducedMotion,
} from "framer-motion";
import {
    Search,
    ArrowRight,
    ArrowUpRight,
    HandHeart,
    Users,
    LineChart,
    Camera,
    Copy,
    Award,
    ShieldCheck,
    BadgeCheck,
    Sparkles,
    Heart,
    Eye,
    Target,
    Leaf,
    Package,
    GraduationCap,
    Activity,
} from "lucide-react";
import SectionReveal from "@/components/SectionReveal";
import SectionHeading from "@/components/SectionHeading";
import CountUp from "@/components/CountUp";
import FocusAreaCard from "@/components/FocusAreaCard";
import OrganicShapes from "@/components/OrganicShapes";
import {
    STATS,
    FOCUS_AREAS,
    CATEGORIES,
} from "@/lib/mockData";
import { fetchPublicStats } from "@/lib/api";
import { USE_MOCK } from "@/lib/config";

const HERO_VIDEO = "/hero-background.mp4"; // looping hero background
const HERO_IMAGE = "/hero-desktop.png"; // wide/landscape — PC view (fallback)
const HERO_IMAGE_MOBILE = "/hero-mobile.png"; // tall/portrait — mobile view (fallback)

const STAT_CONFIG = {
    trees:    { Icon: Leaf,          accentText: "text-seedh-green", accentBg: "bg-seedh-green", tint: "bg-seedh-surface-green" },
    food:     { Icon: Package,       accentText: "text-seedh-brown", accentBg: "bg-seedh-brown", tint: "bg-seedh-surface-beige" },
    children: { Icon: GraduationCap, accentText: "text-seedh-blue",  accentBg: "bg-seedh-blue",  tint: "bg-seedh-surface-blue" },
    drives:   { Icon: Activity,      accentText: "text-seedh-green", accentBg: "bg-seedh-green", tint: "bg-seedh-surface-green" },
};

const HOW_STEPS = [
    {
        n: "01",
        icon: HandHeart,
        title: "Choose your cause",
        body: "Trees, food drives, or child welfare — pick where your contribution should go.",
        tint: "bg-seedh-surface-blue",
        stripe: "bg-seedh-blue",
        accentText: "text-seedh-blue",
        accentBg: "bg-seedh-blue",
        shadow: "shadow-sm hover:shadow-[0_12px_36px_-10px_rgba(4,88,134,0.22)]",
    },
    {
        n: "02",
        icon: Users,
        title: "We organise on-ground",
        body: "Our team runs the drive — planting saplings, cooking meals, distributing supplies.",
        tint: "bg-seedh-surface-green",
        stripe: "bg-seedh-green",
        accentText: "text-seedh-green",
        accentBg: "bg-seedh-green",
        shadow: "shadow-sm hover:shadow-[0_12px_36px_-10px_rgba(56,120,56,0.22)]",
    },
    {
        n: "03",
        icon: Camera,
        title: "Updates are published",
        body: "Photos, status, and field notes are linked to your contribution's unique ID.",
        tint: "bg-seedh-surface-beige",
        stripe: "bg-seedh-brown",
        accentText: "text-seedh-brown",
        accentBg: "bg-seedh-brown",
        shadow: "shadow-sm hover:shadow-[0_12px_36px_-10px_rgba(168,108,64,0.22)]",
    },
    {
        n: "04",
        icon: LineChart,
        title: "You track the impact",
        body: "Anyone can look up the ID and follow the story — forever, publicly.",
        tint: "bg-seedh-surface-blue",
        stripe: "bg-seedh-blue",
        accentText: "text-seedh-blue",
        accentBg: "bg-seedh-blue",
        shadow: "shadow-sm hover:shadow-[0_12px_36px_-10px_rgba(4,88,134,0.22)]",
    },
];

const TRUST_POINTS = [
    {
        icon: BadgeCheck,
        title: "Verified records",
        body: "Every contribution carries a unique, publicly searchable ID.",
    },
    {
        icon: Camera,
        title: "Field updates",
        body: "Photos and status notes added by our team after every drive.",
    },
    {
        icon: ShieldCheck,
        title: "Digital proof",
        body: "On-demand certificates for trees, food drives, and child welfare.",
    },
];

export default function Home() {
    const heroRef = useRef(null);
    const prefersReduced = useReducedMotion();

    // Live homepage stats from MongoDB (backend aggregation). We never show the
    // bundled sample numbers in production — until real values load (or if the
    // backend is unavailable) the figures render as a neutral placeholder.
    const [stats, setStats] = useState(() =>
        STATS.map((s) => ({ ...s, value: USE_MOCK ? s.value : null })),
    );
    useEffect(() => {
        let alive = true;
        fetchPublicStats()
            .then((data) => {
                if (!alive || !data) return;
                const live = {
                    trees: data.trees,
                    food: data.meals,
                    children: data.children,
                    drives: data.drives,
                };
                setStats(
                    STATS.map((s) =>
                        typeof live[s.key] === "number"
                            ? { ...s, value: live[s.key] }
                            : { ...s, value: null },
                    ),
                );
            })
            .catch(() => {
                if (USE_MOCK) setStats(STATS);
            });
        return () => {
            alive = false;
        };
    }, []);

    const { scrollYProgress } = useScroll({
        target: heroRef,
        offset: ["start start", "end start"],
    });

    const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "32%"]);
    const headlineY = useTransform(scrollYProgress, [0, 1], ["0%", "-40%"]);
    const scaleBg = useTransform(scrollYProgress, [0, 1], [1.08, 1.22]);

    return (
        <main data-testid="home-page" className="bg-white">
            {/* HERO */}
            <section
                ref={heroRef}
                data-testid="hero-section"
                className="relative min-h-[100svh] flex items-end overflow-hidden pt-28 isolate"
            >
                <motion.div
                    aria-hidden
                    style={prefersReduced ? {} : { y: bgY, scale: scaleBg }}
                    className="absolute inset-0 z-0 will-change-transform"
                >
                    <picture>
                        {/* Mobile: WebP, then PNG fallback */}
                        <source
                            media="(max-width: 767px)"
                            type="image/webp"
                            srcSet="/hero-mobile.webp"
                        />
                        <source
                            media="(max-width: 767px)"
                            srcSet={HERO_IMAGE_MOBILE}
                        />
                        {/* Desktop: WebP, then PNG fallback (the <img>) */}
                        <source type="image/webp" srcSet="/hero-desktop.webp" />
                        <img
                            src={HERO_IMAGE}
                            alt=""
                            fetchPriority="high"
                            decoding="async"
                            className="w-full h-[120%] object-cover"
                        />
                    </picture>
                    {/* The animated hero, laid over the stills at exactly their
                        geometry. The <picture> above stays as the fallback: it
                        paints first and shows through if the video can't play,
                        so the hero is never empty. */}
                    <video
                        src={HERO_VIDEO}
                        poster={HERO_IMAGE}
                        autoPlay
                        muted
                        loop
                        playsInline
                        preload="metadata"
                        tabIndex={-1}
                        className="absolute inset-x-0 top-0 w-full h-[120%] object-cover"
                    />
                </motion.div>
                <div
                    aria-hidden
                    className="absolute inset-0 z-[1] bg-gradient-to-r from-[#06121b]/97 via-[#06121b]/78 to-[#06121b]/25"
                />
                <div
                    aria-hidden
                    className="absolute inset-0 z-[1] bg-gradient-to-t from-[#06121b]/85 via-[#06121b]/20 to-[#06121b]/30"
                />
                <div className="absolute inset-0 grain z-[2] pointer-events-none opacity-60" />

                <div className="max-w-7xl mx-auto px-6 md:px-10 w-full pb-16 md:pb-28 relative z-10">
                    <motion.div
                        style={prefersReduced ? {} : { y: headlineY }}
                        className="max-w-4xl will-change-transform"
                    >
                        <SectionReveal>
                            <span className="eyebrow eyebrow-light">
                                · Seedh Welfare Society Udaipur ·
                            </span>
                        </SectionReveal>

                        <SectionReveal delay={0.05}>
                            <h1 className="font-display tracking-tight text-white text-5xl sm:text-6xl lg:text-[5.5rem] leading-[0.95] mt-6">
                                Every contribution
                                <br />
                                <span className="font-display-italic text-seedh-surface-green">
                                    carries a story
                                </span>{" "}
                                <span className="text-white">you can follow.</span>
                            </h1>
                        </SectionReveal>

                        <SectionReveal delay={0.12}>
                            <p className="mt-7 text-base md:text-lg text-white/85 max-w-2xl leading-relaxed">
                                A transparent welfare platform across{" "}
                                <span className="font-semibold text-seedh-surface-green">tree plantation</span>,{" "}
                                <span className="font-semibold text-[#F3EEE8]">food drives</span>, and{" "}
                                <span className="font-semibold text-seedh-surface-blue">child welfare</span>.
                                Every drive carries a public ID, traceable for life.
                            </p>
                        </SectionReveal>

                        <SectionReveal delay={0.2}>
                            <div className="mt-10 flex flex-col sm:flex-row gap-4">
                                <Link
                                    to="/tracker"
                                    data-testid="hero-track-cta"
                                    className="rounded-full bg-white text-seedh-blue px-7 py-4 text-sm font-semibold inline-flex items-center justify-center gap-2 group transition-all hover:bg-seedh-surface-blue hover:-translate-y-0.5 shadow-[0_18px_40px_-16px_rgba(255,255,255,0.4)]"
                                >
                                    <Search className="w-4 h-4" />
                                    Track Your Impact
                                    <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                                </Link>
                                <Link
                                    to="/donate"
                                    data-testid="hero-donate-cta"
                                    className="btn-secondary rounded-full px-7 py-4 text-sm font-semibold inline-flex items-center justify-center gap-2 group"
                                >
                                    <HandHeart className="w-4 h-4" />
                                    Choose a Cause
                                    <ArrowUpRight className="w-4 h-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                                </Link>
                            </div>
                        </SectionReveal>

                        <SectionReveal delay={0.32}>
                            <div className="mt-14 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-white/75">
                                <div className="flex items-center gap-2">
                                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-seedh-green animate-pulse" />
                                    Tree Plantation Drives
                                </div>
                                <div className="h-3 w-px bg-white/20" />
                                <div className="flex items-center gap-2">
                                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-seedh-surface-beige" />
                                    Community Food Drives
                                </div>
                                <div className="h-3 w-px bg-white/20" />
                                <div className="flex items-center gap-2">
                                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-seedh-surface-blue" />
                                    Child Welfare
                                </div>
                            </div>
                        </SectionReveal>
                    </motion.div>
                </div>

                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1, duration: 0.8 }}
                    className="absolute bottom-6 left-1/2 -translate-x-1/2 text-[0.7rem] tracking-[0.4em] text-white/55 uppercase flex flex-col items-center gap-2 z-10"
                >
                    Scroll
                    <span className="block w-px h-10 bg-gradient-to-b from-white/50 to-transparent" />
                </motion.div>
            </section>

            {/* LIVE COUNTERS */}
            <section
                data-testid="stats-bar"
                className="relative bg-white border-y border-seedh-border overflow-hidden"
            >
                {/* faint brand wash so pure white doesn't feel flat */}
                <div
                    aria-hidden
                    className="absolute inset-0 pointer-events-none"
                    style={{
                        background:
                            "radial-gradient(120% 90% at 100% 0%, rgba(234,245,232,0.5) 0%, transparent 55%), radial-gradient(110% 90% at 0% 100%, rgba(230,241,247,0.45) 0%, transparent 55%)",
                    }}
                />

                <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-24 relative">

                    {/* Header row */}
                    <div className="flex flex-col md:flex-row md:items-end justify-between gap-5 mb-10 md:mb-14">
                        <div>
                            {/* Pulsing live dot + label */}
                            <div className="inline-flex items-center gap-2.5 text-[0.72rem] tracking-[0.22em] uppercase font-semibold text-seedh-green">
                                <span className="relative flex w-2.5 h-2.5" aria-hidden>
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-seedh-green opacity-60" />
                                    <span className="relative inline-flex rounded-full w-2.5 h-2.5 bg-seedh-green" />
                                </span>
                                Our Impact
                            </div>
                            <h2 className="font-display text-3xl md:text-4xl lg:text-[2.7rem] text-seedh-blue mt-4 leading-[1.05] max-w-xl">
                                Every contribution helps a community grow.
                            </h2>
                        </div>
                        <Link
                            to="/tracker"
                            className="link-underline text-sm text-seedh-blue inline-flex items-center gap-1.5 shrink-0 md:mb-2"
                        >
                            See Verified Impact
                            <ArrowRight className="w-3.5 h-3.5" />
                        </Link>
                    </div>

                    {/* Unified public-record panel — one ledger, four figures */}
                    <div
                        className="rounded-[22px] border border-seedh-border bg-white overflow-hidden"
                        style={{
                            boxShadow:
                                "0 1px 0 rgba(16,32,40,0.02), 0 34px 60px -42px rgba(4,88,134,0.32)",
                        }}
                    >
                        <div className="grid grid-cols-2 lg:grid-cols-4">
                            {stats.map((s, i) => {
                                const cfg = STAT_CONFIG[s.key] ?? STAT_CONFIG.trees;
                                const { Icon } = cfg;
                                return (
                                    <motion.div
                                        key={s.key}
                                        data-testid={`stat-${s.key}`}
                                        initial={
                                            prefersReduced
                                                ? { opacity: 1 }
                                                : { opacity: 0, y: 18 }
                                        }
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true, amount: 0.4 }}
                                        transition={{
                                            duration: 0.7,
                                            ease: [0.22, 1, 0.36, 1],
                                            delay: i * 0.12,
                                        }}
                                        className={`group relative flex flex-col p-6 md:p-8 lg:p-9 border-seedh-border transition-colors duration-300 hover:bg-seedh-surface-beige/45
                                            ${i % 2 === 1 ? "border-l" : ""}
                                            ${i >= 2 ? "border-t" : ""}
                                            lg:border-t-0 ${i !== 0 ? "lg:border-l" : ""}`}
                                    >
                                        {/* Icon */}
                                        <span
                                            aria-hidden
                                            className={`w-11 h-11 rounded-full flex items-center justify-center ${cfg.tint} transition-transform duration-300 group-hover:-translate-y-0.5`}
                                        >
                                            <Icon className={`w-5 h-5 ${cfg.accentText}`} />
                                        </span>

                                        {/* Animated number — unified brand blue */}
                                        <div className="metric-figure text-5xl md:text-6xl text-seedh-blue mt-6">
                                            {typeof s.value === "number" ? (
                                                <CountUp end={s.value} duration={1800 + i * 220} />
                                            ) : (
                                                <span
                                                    aria-hidden
                                                    className="inline-block h-9 md:h-11 w-16 md:w-20 rounded-lg bg-seedh-border/50 animate-pulse align-middle"
                                                />
                                            )}
                                        </div>

                                        {/* Accent underline — the one spot of per-counter colour */}
                                        <div
                                            aria-hidden
                                            className={`h-[3px] w-8 rounded-full mt-4 ${cfg.accentBg} transition-all duration-300 group-hover:w-12`}
                                        />

                                        {/* Label */}
                                        <div className="mt-4 text-[0.7rem] tracking-[0.18em] uppercase text-seedh-muted leading-snug">
                                            {s.label}
                                        </div>
                                    </motion.div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Footer note */}
                    <div className="flex items-center gap-2 mt-6 text-xs text-seedh-muted">
                        <ShieldCheck className="w-3.5 h-3.5 text-seedh-green shrink-0" aria-hidden />
                        Every number represents a publicly verified contribution across our tree plantation, food drives, and child welfare programmes.
                    </div>
                </div>
            </section>

            {/* WHO WE ARE */}
            <section
                id="who-we-are"
                data-testid="intro-section"
                className="relative py-28 md:py-44 overflow-hidden"
            >
                {/* Full-cover background — plantation photo, no resize/crop tricks */}
                <div className="absolute inset-0 z-0">
                    <img
                        src="/sub-hero-image.png"
                        alt=""
                        className="w-full h-full object-cover"
                        style={{ objectPosition: "center calc(50% - 40px)" }}
                        loading="lazy"
                        decoding="async"
                    />
                </div>
                <div aria-hidden className="absolute inset-0 z-[1] bg-black/40" />

                <div className="max-w-7xl mx-auto px-6 md:px-10 relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
                    <div className="lg:col-span-5 flex flex-col items-start">
                        <SectionReveal>
                            <span className="inline-flex items-center gap-3 text-sm font-bold tracking-[0.3em] uppercase text-white">
                                <span className="w-7 h-[2px] rounded-full" style={{ background: "#66B85C" }} />
                                Who we are
                            </span>
                        </SectionReveal>
                        <SectionReveal delay={0.06}>
                            <h2 className="font-display tracking-tight leading-[0.98] mt-6 text-4xl md:text-5xl lg:text-6xl text-white">
                                More than an initiative —
                                <span className="block font-display-italic text-white">
                                    a movement.
                                </span>
                            </h2>
                        </SectionReveal>
                    </div>
                    <div className="lg:col-span-7">
                        <SectionReveal>
                            <p className="text-base md:text-lg leading-relaxed" style={{ color: "rgba(255,255,255,0.75)" }}>
                                Every sapling we nurture is a quiet promise to the
                                earth. Every meal we serve is dignity restored to a
                                family in need. Every child we stand beside is a
                                future we choose to protect. At Seedh, we believe
                                compassion is most powerful when it moves — from
                                feeling to action, from one hand to many.
                            </p>
                        </SectionReveal>
                        <SectionReveal delay={0.1}>
                            <blockquote className="mt-8 pl-6 font-display-italic text-xl md:text-2xl text-white leading-snug" style={{ borderLeft: "2px solid #66B85C" }}>
                                "Our name, Seedh, stands for clarity, direction,
                                and growth — a straight path forward that unites
                                people, ideas, and action for a more sustainable
                                and inclusive future."
                            </blockquote>
                        </SectionReveal>
                    </div>
                </div>
            </section>

            {/* THREE FOCUS AREAS */}
            <section
                id="focus-areas"
                data-testid="focus-areas-section"
                className="relative py-24 md:py-32 bg-white overflow-hidden"
            >
                <OrganicShapes variant="mixed" />
                <div className="max-w-7xl mx-auto px-6 md:px-10 relative">
                    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-16">
                        <SectionHeading
                            eyebrow="Three focus areas"
                            heading={[
                                "Three branches —",
                                "one transparent route.",
                            ]}
                            italicIndex={1}
                            description="Behind every tree stands a volunteer. Behind every meal, a family given hope. Behind every child supported, a community made stronger. These are the three causes we dedicate ourselves to — and every rupee you give is recorded, traced, and visible, always."
                        />
                        <SectionReveal delay={0.15}>
                            <Link
                                to="/how-it-works"
                                className="link-underline text-sm text-seedh-blue inline-flex items-center gap-1.5"
                            >
                                See how it works
                                <ArrowRight className="w-3.5 h-3.5" />
                            </Link>
                        </SectionReveal>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {FOCUS_AREAS.map((fa, i) => (
                            <FocusAreaCard
                                key={fa.category}
                                data={fa}
                                index={i}
                            />
                        ))}
                    </div>
                </div>
            </section>

            {/* TRANSPARENCY PROMISE */}
            <section
                data-testid="transparency-section"
                className="relative py-16 md:py-24 overflow-hidden border-b border-seedh-border"
                style={{ background: "linear-gradient(155deg, #FBF8F2 0%, #F4EFE6 55%, #EEF6EE 100%)" }}
            >
                {/* Soft radial warmth accents */}
                <div aria-hidden className="absolute inset-0 pointer-events-none" style={{
                    background: "radial-gradient(ellipse 60% 50% at 5% 50%, rgba(74,154,65,0.07) 0%, transparent 60%), radial-gradient(ellipse 50% 45% at 95% 50%, rgba(4,88,134,0.06) 0%, transparent 60%)"
                }} />

                <div className="max-w-7xl mx-auto px-6 md:px-10 relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">

                    {/* Left — heading */}
                    <div className="lg:col-span-5">
                        <SectionReveal>
                            <span className="inline-flex items-center gap-3 text-[0.7rem] tracking-[0.28em] uppercase font-bold text-seedh-brown">
                                <span className="w-7 h-[2px] rounded-full bg-seedh-brown" />
                                Transparency Promise
                            </span>
                        </SectionReveal>
                        <SectionReveal delay={0.06}>
                            <h2 className="font-display text-4xl md:text-5xl text-seedh-blue mt-5 leading-[1.05]">
                                Nothing disappears
                                <span className="block font-display-italic text-seedh-green">after the donation.</span>
                            </h2>
                        </SectionReveal>
                        <SectionReveal delay={0.1}>
                            <p className="mt-5 text-base text-seedh-muted leading-relaxed">
                                Every tree, food packet, and stationery kit we hand out gets linked to a public ID. Donors and the public can verify any contribution — anytime, no login.
                            </p>
                        </SectionReveal>
                    </div>

                    {/* Right — pastel feature cards */}
                    <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {[
                            { icon: BadgeCheck, t: "Unique IDs",          d: "Tree, Food, and Child Welfare IDs published before drives even start.",   bg: "#EAF5E8", border: "#4A9A41", accent: "#4A9A41", iconBg: "#D4EDCF" },
                            { icon: Camera,     t: "Photo timelines",      d: "Each ID carries a chronological log of field updates.",               bg: "#E6F1F7", border: "#045886", accent: "#045886", iconBg: "#CCE2F0" },
                            { icon: Sparkles,   t: "Replacement records",  d: "If a tree dies, its replacement is tracked under a suffixed ID.",     bg: "#F3EEE8", border: "#8B5E34", accent: "#8B5E34", iconBg: "#E8D9C8" },
                            { icon: Award,      t: "Digital certificates", d: "On-demand certificates for any verified contribution.",               bg: "#EAF5E8", border: "#4A9A41", accent: "#4A9A41", iconBg: "#D4EDCF" },
                        ].map((b, i) => (
                            <SectionReveal delay={i * 0.08} key={b.t}>
                                <motion.div
                                    whileHover={{ y: -4, boxShadow: "0 12px 36px rgba(0,0,0,0.10)" }}
                                    transition={{ duration: 0.22, ease: "easeOut" }}
                                    className="p-6 h-full rounded-2xl border-t-[3px]"
                                    style={{
                                        background: b.bg,
                                        borderTopColor: b.border,
                                        borderLeft: `1px solid ${b.border}30`,
                                        borderRight: `1px solid ${b.border}30`,
                                        borderBottom: `1px solid ${b.border}30`,
                                        boxShadow: "0 2px 12px rgba(0,0,0,0.05)",
                                    }}
                                >
                                    <span className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: b.iconBg }}>
                                        <b.icon className="w-5 h-5" style={{ color: b.accent }} />
                                    </span>
                                    <div className="font-display text-lg text-seedh-text mt-4 leading-tight">
                                        {b.t}
                                    </div>
                                    <div className="h-[2px] w-7 rounded-full mt-2" style={{ background: b.accent }} />
                                    <p className="text-sm text-seedh-muted mt-3 leading-relaxed">
                                        {b.d}
                                    </p>
                                </motion.div>
                            </SectionReveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* VISION & MISSION */}
            <section
                id="vision-mission"
                data-testid="vision-mission-section"
                className="relative py-24 md:py-32 overflow-hidden"
            >
                {/* Background image */}
                <div className="absolute inset-0 z-0">
                    <img
                        src="/vision-mission-bg.png"
                        alt=""
                        className="w-full h-full object-cover object-center"
                        loading="lazy"
                        decoding="async"
                    />
                </div>
                {/* Natural dark-green gradient overlay — lets children + greenery show through */}
                <div
                    aria-hidden
                    className="absolute inset-0 z-[1]"
                    style={{
                        background:
                            "linear-gradient(90deg, rgba(16,36,25,0.66) 0%, rgba(20,42,30,0.48) 50%, rgba(16,36,25,0.64) 100%)",
                    }}
                />

                <div className="max-w-7xl mx-auto px-6 md:px-10 relative z-10">

                    {/* Section heading */}
                    <SectionReveal>
                        <span className="inline-flex items-center gap-3 text-sm font-bold tracking-[0.32em] uppercase text-[#f0a050]">
                            <span className="w-8 h-px bg-[#f0a050]" />
                            Vision &amp; Mission
                        </span>
                    </SectionReveal>
                    <SectionReveal delay={0.06}>
                        <h2 className="font-display tracking-tight leading-[0.98] mt-5 text-4xl md:text-5xl lg:text-6xl text-white">
                            The route we're on,
                            <span className="block font-display-italic text-seedh-surface-green">
                                and the promise behind it.
                            </span>
                        </h2>
                    </SectionReveal>
                    <SectionReveal delay={0.12}>
                        <p className="mt-5 text-base md:text-lg text-white/70 max-w-2xl leading-relaxed">
                            What we're working toward, and how we get there — the beliefs that guide every drive we run.
                        </p>
                    </SectionReveal>

                    {/* Cards grid — equal height, 24px gap on mobile / 28px on desktop */}
                    <div className="mt-14 grid grid-cols-1 lg:grid-cols-2 gap-5 lg:gap-7 items-stretch">

                        {/* VISION card — forest green accent */}
                        <SectionReveal>
                            <motion.div
                                whileHover={{ y: -5, boxShadow: "0 24px 60px rgba(20,45,30,0.22)" }}
                                transition={{ duration: 0.25, ease: "easeOut" }}
                                className="flex flex-col h-full motion-reduce:transform-none motion-reduce:transition-none"
                                style={{
                                    background: "#F7F5EE",
                                    borderRadius: "22px",
                                    borderTop: "4px solid #3F8F4E",
                                    padding: "clamp(24px,3vw,34px) clamp(20px,3vw,36px)",
                                    boxShadow: "0 18px 50px rgba(20,45,30,0.16)",
                                }}
                            >
                                {/* Icon */}
                                <span
                                    className="flex items-center justify-center shrink-0"
                                    style={{
                                        width: 46, height: 46,
                                        borderRadius: "50%",
                                        background: "#E6F3E6",
                                    }}
                                >
                                    <Eye className="w-5 h-5" style={{ color: "#276738" }} />
                                </span>

                                {/* Label */}
                                <div
                                    className="inline-flex items-center gap-2 mt-5"
                                    style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.18em", textTransform: "uppercase", color: "#3F8F4E" }}
                                >
                                    <span className="w-5 h-px" style={{ background: "#3F8F4E" }} />
                                    Our Vision
                                </div>

                                {/* Title */}
                                <h3
                                    className="font-display mt-3 leading-[1.18]"
                                    style={{ fontSize: "clamp(24px,2vw,29px)", fontWeight: 600, color: "#18251D" }}
                                >
                                    To protect the environment and nurture a greener planet for future generations.
                                </h3>

                                {/* Description */}
                                <p
                                    className="mt-4 flex-1"
                                    style={{ fontSize: 15, lineHeight: 1.65, color: "#526158" }}
                                >
                                    We envision a world where every individual feels responsible and empowered to contribute toward restoring Earth's natural balance — through plantation, preservation, and purposeful action.
                                </p>

                                {/* Quote panel */}
                                <div
                                    className="mt-auto pt-5"
                                >
                                    <div
                                        style={{
                                            background: "#E6F3E6",
                                            borderLeft: "3px solid #3F8F4E",
                                            borderRadius: 8,
                                            padding: "12px 14px",
                                            fontSize: 14,
                                            fontWeight: 500,
                                            lineHeight: 1.5,
                                            color: "#276738",
                                        }}
                                        className="font-display-italic"
                                    >
                                        "For the Earth we live on. For the future we leave behind."
                                    </div>
                                </div>
                            </motion.div>
                        </SectionReveal>

                        {/* MISSION card — earthy orange accent */}
                        <SectionReveal delay={0.1}>
                            <motion.div
                                whileHover={{ y: -5, boxShadow: "0 24px 60px rgba(20,45,30,0.22)" }}
                                transition={{ duration: 0.25, ease: "easeOut" }}
                                className="flex flex-col h-full motion-reduce:transform-none motion-reduce:transition-none"
                                style={{
                                    background: "#F7F5EE",
                                    borderRadius: "22px",
                                    borderTop: "4px solid #D8752B",
                                    padding: "clamp(24px,3vw,34px) clamp(20px,3vw,36px)",
                                    boxShadow: "0 18px 50px rgba(20,45,30,0.16)",
                                }}
                            >
                                {/* Icon */}
                                <span
                                    className="flex items-center justify-center shrink-0"
                                    style={{
                                        width: 46, height: 46,
                                        borderRadius: "50%",
                                        background: "#FCEBDD",
                                    }}
                                >
                                    <Target className="w-5 h-5" style={{ color: "#A9531D" }} />
                                </span>

                                {/* Label */}
                                <div
                                    className="inline-flex items-center gap-2 mt-5"
                                    style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.18em", textTransform: "uppercase", color: "#D8752B" }}
                                >
                                    <span className="w-5 h-px" style={{ background: "#D8752B" }} />
                                    Our Mission
                                </div>

                                {/* Title */}
                                <h3
                                    className="font-display mt-3 leading-[1.18]"
                                    style={{ fontSize: "clamp(24px,2vw,29px)", fontWeight: 600, color: "#18251D" }}
                                >
                                    To act as a bridge between intent and action.
                                </h3>

                                {/* Description */}
                                <p
                                    className="mt-4 flex-1"
                                    style={{ fontSize: 15, lineHeight: 1.65, color: "#526158" }}
                                >
                                    We reach those who care for the environment but can't always contribute amid the demands of daily life. Through organized action and sustained manpower, Seedh carries their intentions forward — working collectively to preserve, protect, and nurture our planet.
                                </p>

                                {/* Quote panel */}
                                <div className="mt-auto pt-5">
                                    <div
                                        style={{
                                            background: "#FCEBDD",
                                            borderLeft: "3px solid #D8752B",
                                            borderRadius: 8,
                                            padding: "12px 14px",
                                            fontSize: 14,
                                            fontWeight: 500,
                                            lineHeight: 1.5,
                                            color: "#A9531D",
                                        }}
                                        className="font-display-italic"
                                    >
                                        "You wish it. We plant it. Together, we protect it."
                                    </div>
                                </div>
                            </motion.div>
                        </SectionReveal>

                    </div>
                </div>
            </section>

            {/* HOW IT WORKS */}
            <section
                id="how-it-works"
                data-testid="how-it-works-section"
                className="relative py-16 md:py-24 overflow-hidden border-b border-seedh-border"
                style={{
                    background: "linear-gradient(155deg, #FBF8F2 0%, #F4EFE6 55%, #EEF6EE 100%)",
                }}
            >
                {/* Faint organic dot texture */}
                <div
                    aria-hidden
                    className="absolute inset-0 pointer-events-none opacity-[0.045]"
                    style={{
                        backgroundImage:
                            "radial-gradient(circle at 20% 30%, #4A9A41 1px, transparent 1px), radial-gradient(circle at 75% 60%, #045886 1px, transparent 1px), radial-gradient(circle at 50% 80%, #4A9A41 1px, transparent 1px)",
                        backgroundSize: "60px 60px, 80px 80px, 50px 50px",
                    }}
                />

                <div className="max-w-7xl mx-auto px-6 md:px-10 relative z-20">
                    <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16">
                        <SectionHeading
                            eyebrow="How it works"
                            heading={[
                                "Four quiet steps —",
                                "from intent to impact.",
                            ]}
                            italicIndex={1}
                        />
                        <SectionReveal delay={0.1}>
                            <Link
                                to="/how-it-works"
                                data-testid="see-process-link"
                                className="link-underline text-sm text-seedh-blue inline-flex items-center gap-1.5"
                            >
                                See the full process
                                <ArrowRight className="w-3.5 h-3.5" />
                            </Link>
                        </SectionReveal>
                    </div>

                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.15 }}
                        variants={{
                            visible: { transition: { staggerChildren: 0.12 } },
                        }}
                        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5"
                    >
                        {HOW_STEPS.map((s, i) => (
                            <motion.div
                                key={s.n}
                                variants={{
                                    hidden: { opacity: 0, y: 30 },
                                    visible: {
                                        opacity: 1,
                                        y: 0,
                                        transition: {
                                            duration: 0.8,
                                            ease: [0.22, 1, 0.36, 1],
                                        },
                                    },
                                }}
                                whileHover={{ y: -6 }}
                                className={`relative overflow-hidden flex flex-col min-h-[300px] rounded-[18px] border border-seedh-border/70 ${s.tint} ${s.shadow} transition-all duration-300`}
                                style={{ boxShadow: "0 2px 16px rgba(74,154,65,0.07), 0 1px 3px rgba(4,88,134,0.06)" }}
                                data-testid={`how-step-${i + 1}`}
                            >
                                {/* Colored top accent stripe */}
                                <div className={`absolute top-0 left-0 right-0 h-[3px] ${s.stripe}`} />

                                {/* Subtle gloss — reduced so tint colour shows through */}
                                <div
                                    aria-hidden
                                    className="absolute inset-0 pointer-events-none"
                                    style={{
                                        background:
                                            "linear-gradient(135deg, rgba(255,255,255,0.30) 0%, rgba(255,255,255,0.05) 50%, transparent 75%)",
                                    }}
                                />

                                <div className="p-7 flex flex-col flex-1 relative z-10">
                                    <div className="flex items-start justify-between">
                                        <span className={`font-display text-5xl ${s.accentText} opacity-30`}>
                                            {s.n}
                                        </span>
                                        <span className="w-10 h-10 rounded-full bg-white border border-seedh-border flex items-center justify-center shadow-sm">
                                            <s.icon className={`w-5 h-5 ${s.accentText}`} />
                                        </span>
                                    </div>
                                    <h3 className="font-display text-2xl text-seedh-text mt-6 leading-tight">
                                        {s.title}
                                    </h3>
                                    <p className="text-sm text-seedh-muted mt-3 leading-relaxed">
                                        {s.body}
                                    </p>

                                    <div className="flex-1" />

                                    <div className="flex justify-center mt-6">
                                        <span className={`inline-flex items-center px-4 py-1.5 rounded-full text-[0.65rem] tracking-[0.24em] uppercase font-semibold text-white ${s.accentBg}`}>
                                            Step {s.n}
                                        </span>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </motion.div>
                </div>
            </section>


            {/* DONATE CTA */}
            <section
                id="donate"
                data-testid="donate-section"
                className="relative py-24 md:py-32 overflow-hidden"
                style={{
                    background: `
                        radial-gradient(ellipse 70% 65% at -8% 108%, rgba(45,122,45,0.30) 0%, transparent 65%),
                        radial-gradient(ellipse 65% 60% at 108% -8%, rgba(4,88,134,0.24) 0%, transparent 65%),
                        radial-gradient(ellipse 55% 50% at 85% 90%, rgba(168,108,64,0.13) 0%, transparent 60%),
                        radial-gradient(ellipse 60% 55% at 15% 10%, rgba(180,220,165,0.22) 0%, transparent 60%),
                        #e8ede5
                    `.replace(/\s+/g, " ").trim()
                }}
            >
                <OrganicShapes variant="green" />
                <div
                    aria-hidden
                    className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full bg-seedh-green/8 blur-3xl pointer-events-none"
                />
                <div
                    aria-hidden
                    className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full bg-seedh-blue/6 blur-3xl pointer-events-none"
                />

                <div className="max-w-7xl mx-auto px-6 md:px-10 relative z-10">

                    {/* Heading spans full width */}
                    <SectionHeading
                        eyebrow="Choose a cause"
                        heading={[
                            "Support something",
                            "that outlives all of us.",
                        ]}
                        italicIndex={1}
                        description="Pick a cause — trees, food drives, or child welfare — and we'll map your contribution to a public ID with photo updates and a digital certificate."
                        maxWidth="max-w-5xl"
                    />

                    {/* Two-col grid starts below heading — QR aligns with cards */}
                    <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-stretch">
                    <div className="lg:col-span-7">

                        {/* Cause cards — colored border accent on light bg */}
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                            {Object.values(CATEGORIES).map((c) => {
                                const Icon = c.icon;
                                return (
                                    <Link
                                        key={c.key}
                                        to={`/donate?cause=${c.key}`}
                                        data-testid={`donate-cause-${c.key}`}
                                        className={`group rounded-2xl bg-white border ${c.tintBorder} p-5 shadow-sm hover:shadow-md transition-all hover:-translate-y-0.5 relative overflow-hidden`}
                                    >
                                        <div className={`absolute top-0 left-0 right-0 h-[3px] ${c.accentBg}`} />
                                        <span className={`w-9 h-9 rounded-full ${c.tintBg} flex items-center justify-center`}>
                                            <Icon className={`w-4 h-4 ${c.accentText}`} />
                                        </span>
                                        <div className="font-display text-lg text-seedh-text mt-3 leading-tight">
                                            {c.label}
                                        </div>
                                        <div className="text-xs text-seedh-muted mt-2 leading-relaxed">
                                            Traceable proof for every contribution.
                                        </div>
                                        <div className={`mt-4 inline-flex items-center gap-1 text-xs font-semibold ${c.accentText}`}>
                                            Choose
                                            <ArrowUpRight className="w-3 h-3 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                                        </div>
                                    </Link>
                                );
                            })}
                        </div>

                        {/* Trust points — distinctly different: stacked bordered list */}
                        <div className="mt-10 rounded-2xl border border-seedh-border bg-white shadow-sm overflow-hidden">
                            <div className="px-5 py-3 bg-seedh-surface-beige border-b border-seedh-border">
                                <span className="text-[0.65rem] tracking-[0.22em] uppercase text-seedh-brown font-semibold">
                                    Why your contribution is safe with us
                                </span>
                            </div>
                            {TRUST_POINTS.map((t, i) => (
                                <div
                                    key={t.title}
                                    className={`flex items-start gap-4 px-5 py-4 ${i < TRUST_POINTS.length - 1 ? "border-b border-seedh-border" : ""}`}
                                >
                                    <span className="w-8 h-8 rounded-full bg-seedh-surface-green border border-seedh-border flex items-center justify-center shrink-0 mt-0.5">
                                        <t.icon className="w-3.5 h-3.5 text-seedh-green" />
                                    </span>
                                    <div>
                                        <div className="text-sm font-semibold text-seedh-text">{t.title}</div>
                                        <div className="text-xs text-seedh-muted mt-0.5 leading-relaxed">{t.body}</div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="mt-10">
                            <Link
                                to="/donate"
                                data-testid="donate-form-cta"
                                className="group w-full flex items-center justify-center gap-3 rounded-full bg-seedh-blue text-white px-12 py-5 text-base font-semibold shadow-[0_8px_32px_-8px_rgba(4,88,134,0.45)] hover:shadow-[0_12px_40px_-8px_rgba(4,88,134,0.6)] hover:-translate-y-0.5 transition-all duration-300"
                            >
                                <Heart className="w-5 h-5 text-seedh-surface-green transition-transform group-hover:scale-110" />
                                Open Donation Page
                                <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
                            </Link>
                        </div>
                    </div>

                    {/* QR / UPI card — aligned with cause cards */}
                    <SectionReveal delay={0.15} className="lg:col-span-5 h-full">
                        <div className="card-base bg-white p-7 shadow-lg relative overflow-hidden h-full flex flex-col">
                            <div className="absolute top-0 left-0 right-0 h-[3px] bg-seedh-green" />
                            <div className="flex items-center justify-between">
                                <span className="eyebrow">UPI · Quick pay</span>
                                <Award className="w-5 h-5 text-seedh-brown" />
                            </div>

                            {/* Real QR code image */}
                            <div className="mt-6 flex flex-col items-center">
                                <div className="rounded-2xl border border-seedh-border p-3 bg-white shadow-sm inline-block">
                                    <img
                                        src="/upi-qr.jpg"
                                        alt="Scan to pay Seedh Welfare Society via UPI"
                                        className="w-48 h-48 object-contain"
                                    />
                                </div>
                                <span className="mt-3 pill bg-seedh-green text-white text-xs">
                                    Scan to pay
                                </span>
                            </div>

                            <div className="flex-1" />
                            <div className="mt-6 rounded-xl bg-seedh-surface-blue border border-seedh-border px-4 py-3 flex items-center justify-between">
                                <div>
                                    <div className="text-[0.65rem] uppercase tracking-[0.22em] text-seedh-brown">
                                        UPI ID
                                    </div>
                                    <div className="font-mono text-sm text-seedh-blue mt-1">
                                        7340510611@sbi
                                    </div>
                                </div>
                                <button
                                    data-testid="copy-upi"
                                    onClick={() => navigator.clipboard?.writeText("7340510611@sbi")}
                                    className="w-9 h-9 rounded-full bg-white border border-seedh-border flex items-center justify-center text-seedh-blue hover:bg-seedh-surface-blue transition-colors"
                                    aria-label="Copy UPI ID"
                                >
                                    <Copy className="w-4 h-4" />
                                </button>
                            </div>

                            <p className="mt-5 text-[0.72rem] text-seedh-muted leading-relaxed">
                                After payment, head to the donation page and share your transaction reference so we can link it to a record.
                            </p>
                        </div>
                    </SectionReveal>
                    </div>{/* end two-col grid */}
                </div>{/* end max-w container */}
            </section>

            {/* FINAL CTA */}
            <section
                data-testid="final-cta-section"
                className="relative bg-white py-20 md:py-28 overflow-hidden"
            >
                <OrganicShapes variant="beige" />
                <div className="max-w-5xl mx-auto px-6 md:px-10 relative text-center">
                    <SectionReveal>
                        <span className="eyebrow justify-center">Join the route</span>
                    </SectionReveal>
                    <SectionReveal delay={0.05}>
                        <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-seedh-blue tracking-tight leading-[0.98] mt-6">
                            From trees to food to education —{" "}
                            <span className="font-display-italic text-seedh-green">
                                every contribution is traceable.
                            </span>
                        </h2>
                    </SectionReveal>
                    <SectionReveal delay={0.15}>
                        <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
                            <Link
                                to="/donate"
                                className="btn-primary rounded-full px-7 py-4 text-sm font-semibold inline-flex items-center justify-center gap-2"
                            >
                                <Heart className="w-4 h-4" />
                                Support a Cause
                            </Link>
                            <Link
                                to="/tracker"
                                className="btn-outline rounded-full px-7 py-4 text-sm font-semibold inline-flex items-center justify-center gap-2"
                            >
                                <Search className="w-4 h-4" />
                                Track Your Impact
                            </Link>
                        </div>
                    </SectionReveal>
                </div>
            </section>
        </main>
    );
}
